import React from 'react';
import { RDV_PREALABLE_INFO } from '../data/bilanReprise';
import { AlertTriangle, HelpCircle, ShieldCheck, Hand } from 'lucide-react';

export default function RdvPrealableGuide() {
  const O = RDV_PREALABLE_INFO.obligations;
  const I = RDV_PREALABLE_INFO.infractions;
  const S = RDV_PREALABLE_INFO.sansPedales;
  return (
    <div className="space-y-3">
      {/* Accompagner sans pédales */}
      <details className="rounded-xl border border-sky-500/25 bg-sky-950/10 overflow-hidden" data-testid="rdv-guide-sans-pedales" open>
        <summary className="px-4 py-3 cursor-pointer text-sky-200 text-[12.5px] font-bold font-mono-spec uppercase tracking-wider hover:bg-sky-500/[0.06] inline-flex items-center gap-2 w-full">
          <Hand size={13} className="text-sky-400" /> Accompagner SANS pédales · comment gérer et intervenir ?
        </summary>
        <div className="px-4 pb-4">
          <p className="mt-1 text-white/70 text-[12.5px] leading-relaxed italic">{S.intro}</p>
          <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.02] p-3">
            <div className="text-sky-300 text-[11px] font-bold font-mono-spec uppercase tracking-wider mb-1.5">Gestion au quotidien</div>
            <ul className="space-y-1.5">
              {S.conseils.map((c, i) => (
                <li key={i} className="flex gap-2 text-white/80 text-[12.5px] leading-snug">
                  <span className="shrink-0 mt-0.5 text-sky-400">•</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-2.5 rounded-lg border border-red-500/30 bg-red-950/15 p-3">
            <div className="text-red-300 text-[11px] font-bold font-mono-spec uppercase tracking-wider mb-1.5">En cas d&apos;urgence (sans double commande)</div>
            <ul className="space-y-1.5">
              {S.urgence.map((u, i) => (
                <li key={i} className="flex gap-2 text-red-100/85 text-[12.5px] leading-snug">
                  <span className="shrink-0 mt-0.5 text-red-400 font-bold">{i + 1}.</span>
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </details>

      {/* Obligations officielles de l'accompagnateur */}
      <details className="rounded-xl border border-emerald-500/25 bg-emerald-950/10 overflow-hidden" data-testid="rdv-guide-obligations">
        <summary className="px-4 py-3 cursor-pointer text-emerald-200 text-[12.5px] font-bold font-mono-spec uppercase tracking-wider hover:bg-emerald-500/[0.06] inline-flex items-center gap-2 w-full">
          <ShieldCheck size={13} className="text-emerald-400" /> Obligations officielles de l&apos;accompagnateur
        </summary>
        <div className="px-4 pb-4">
          <p className="text-white/40 text-[11px] font-mono-spec">{O.source}</p>
          <div className="mt-3 grid md:grid-cols-2 gap-2.5">
            <GuideList titre="Conditions pour être accompagnateur" items={O.conditions} />
            <GuideList titre="Pendant la conduite accompagnée" items={O.pendant} />
            <GuideList titre="Vitesses maximales (conducteur novice)" items={O.vitesses} />
            <GuideList titre="Documents à présenter en cas de contrôle" items={O.documents} />
          </div>
          <div className="mt-2.5">
            <GuideList titre="Bon à savoir" items={O.divers} />
          </div>
        </div>
      </details>

      {/* Infractions & responsabilités */}
      <details className="rounded-xl border border-red-500/25 bg-red-950/10 overflow-hidden" data-testid="rdv-guide-infractions">
        <summary className="px-4 py-3 cursor-pointer text-red-200 text-[12.5px] font-bold font-mono-spec uppercase tracking-wider hover:bg-red-500/[0.06] inline-flex items-center gap-2 w-full">
          <AlertTriangle size={13} className="text-red-400" /> Infractions en AAC · qui est responsable, quelles sanctions ?
        </summary>
        <div className="px-4 pb-4">
          <div className="mt-1 rounded-lg border border-white/10 bg-white/[0.03] p-3 text-white/80 text-[12.5px] leading-relaxed">
            <strong className="text-white">Le principe :</strong> {I.principe}
          </div>
          <div className="mt-3 grid md:grid-cols-2 gap-2.5">
            {I.responsabilites.map((r, i) => (
              <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                <div className="text-red-300 text-[11.5px] font-bold mb-1">{r.titre}</div>
                <p className="text-white/65 text-[12px] leading-relaxed">{r.texte}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-lg border border-white/10 overflow-hidden overflow-x-auto">
            <table className="w-full text-[11.5px] min-w-[560px]">
              <thead>
                <tr className="bg-white/5 text-left">
                  <th className="px-3 py-2 text-white/50 font-mono-spec uppercase text-[9.5px] tracking-wider">Infraction</th>
                  <th className="px-3 py-2 text-white/50 font-mono-spec uppercase text-[9.5px] tracking-wider">Amende</th>
                  <th className="px-3 py-2 text-white/50 font-mono-spec uppercase text-[9.5px] tracking-wider">Points (accompagnateur / carte grise)</th>
                  <th className="px-3 py-2 text-white/50 font-mono-spec uppercase text-[9.5px] tracking-wider">Conséquence élève</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {I.tableau.map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-white/85 font-semibold">{r.infraction}</td>
                    <td className="px-3 py-2 text-white/70">{r.amende}</td>
                    <td className="px-3 py-2 text-amber-300/90">{r.points}</td>
                    <td className="px-3 py-2 text-white/60">{r.eleve}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 space-y-2">
            {I.delits.map((d, i) => (
              <div key={i} className="rounded-lg border border-red-500/30 bg-red-950/15 p-3">
                <div className="text-red-300 text-[11.5px] font-bold mb-1">⚠ {d.titre}</div>
                <p className="text-red-100/75 text-[12px] leading-relaxed">{d.texte}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-lg border border-amber-600/30 bg-amber-950/10 p-3">
            <div className="text-amber-300 text-[11px] font-bold font-mono-spec uppercase tracking-wider mb-1.5">Conséquences pour le futur permis de l&apos;élève</div>
            <ul className="space-y-1">
              {I.consequences_eleve.map((c, i) => (
                <li key={i} className="flex gap-2 text-amber-100/85 text-[12px] leading-snug">
                  <span className="shrink-0 mt-0.5 text-amber-400">→</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </details>

      {/* FAQ */}
      <details className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="rdv-guide-faq">
        <summary className="px-4 py-3 cursor-pointer text-white/80 text-[12.5px] font-bold font-mono-spec uppercase tracking-wider hover:bg-white/[0.04] inline-flex items-center gap-2 w-full">
          <HelpCircle size={13} className="text-sky-400" /> FAQ · Questions fréquentes des élèves et des parents ({RDV_PREALABLE_INFO.faq.length})
        </summary>
        <div className="px-4 pb-4 divide-y divide-white/5">
          {RDV_PREALABLE_INFO.faq.map((f, i) => (
            <div key={i} className="py-3">
              <div className="text-sky-200 text-[13px] font-semibold">{f.q}</div>
              <p className="mt-1 text-white/65 text-[12.5px] leading-relaxed">{f.r}</p>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

function GuideList({ titre, items }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
      <div className="text-emerald-300 text-[11px] font-bold font-mono-spec uppercase tracking-wider mb-1.5">{titre}</div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2 text-white/75 text-[12px] leading-snug">
            <span className="shrink-0 mt-0.5 text-emerald-400">•</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
